import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { styles as globalStyles } from '../../../utils/styles';
import { DownArrow, DragIcon, OverFlowIcon, UpArrow } from 'assests/GmsIcons';
import Slider from 'Pages/GMS/ABTesting/components/Slider';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import { useEditor } from '../../../contexts/EditorContext';
import { GroupType, PagesConfigType } from '../../../types/navigation.types';
import { useNavigationOperations } from '../../../hooks/useNavigationOperations';
import { toast } from 'react-toastify';
import { DropdownMenu, DropdownMenuItem } from '../../Common/DropdownMenu';
import { ConfirmationDialog } from '../../Common/ConfirmationDialog';
import { useUIModelApi } from '../../../contexts/UIModelApiContext';
import { uiModelApiService } from '../../../services/UIModelApiService';

type DeleteConfirmationState = {
    isOpen: boolean;
    type: 'group' | 'page' | null;
    id: string | null;
    parentId?: string | null;
    name: string;
}

type PagesConfigProps = {
    id?: string;
    initialConfig?: Partial<PagesConfigType>;
    onConfigChange?: (config: PagesConfigType) => void;
}

const DeleteIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z" 
            stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const DuplicateIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="5" width="9" height="9" rx="1" stroke="#172B4D" strokeWidth="1.5"/>
        <path d="M11 5V3C11 2.44772 10.5523 2 10 2H3C2.44772 2 2 2.44772 2 3V10C2 10.5523 2.44772 11 3 11H5" stroke="#172B4D" strokeWidth="1.5"/>
    </svg>
);

export const PagesConfig = ({ id, initialConfig, onConfigChange }: PagesConfigProps) => {
    const baseId = id || 'pages-config';
    const { groups, setGroups, editorState, setEditorState, setContainers } = useEditor();
    const { canvasMap, setCanvasMap, setCanvas, configureId, currentUIModelId } = useUIModelApi();

    const [isExpanded, setIsExpanded] = useState(true);
    
    const { deleteGroup, deletePage, duplicatePage } = useNavigationOperations(editorState.activeGroupId || '');
    const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState>({
        isOpen: false,
        type: null,
        id: null,
        parentId: null,
        name: ''
    });
    const [isDeleting, setIsDeleting] = useState(false);

    const pageSpacingTimerRef = useRef<NodeJS.Timeout>();
    const groupSpacingTimerRef = useRef<NodeJS.Timeout>();
    
    const [localPageSpacing, setLocalPageSpacing] = useState<number>(initialConfig?.pageSpacing || 10);
    const [localGroupSpacing, setLocalGroupSpacing] = useState<number>(initialConfig?.groupSpacing || 10);

    const convertGroupsToConfigFormat = (): GroupType[] => {
        return groups.map(group => ({
            id: group.id,
            name: group.name,
            pages: group.pages.map(tab => ({
                id: tab.id,
                name: tab.name
            }))
        }));
    };

    const defaultConfig: PagesConfigType = {
        id: initialConfig?.id || 'global-navigation',
        orientation: 'Horizontal',
        style: 'Block',
        flow: 'Nonlinear',
        textAlignment: 'Centre',
        showDivider: true,
        showNavigation: true,
        pageSpacing: 10,
        groupSpacing: 10,
        groups: []
    };

    const [config, setConfig] = useState<PagesConfigType>({
        ...defaultConfig,
        ...initialConfig,
        groups: []
    });

    useEffect(() => {
        setConfig(prevConfig => ({
            ...prevConfig,
            groups: convertGroupsToConfigFormat()
        }));
    }, [groups]);

    useEffect(() => {
        setLocalPageSpacing(config.pageSpacing);
        setLocalGroupSpacing(config.groupSpacing);
    }, [config.pageSpacing, config.groupSpacing]);

    useEffect(() => {
        if (initialConfig) {
            setConfig(prevConfig => ({
                ...prevConfig,
                ...initialConfig,
                id: initialConfig.id || prevConfig.id,
                groups: prevConfig.groups && prevConfig.groups.length > 0 
                    ? prevConfig.groups 
                    : convertGroupsToConfigFormat()
            }));
        }
    }, [initialConfig]);

    const updateConfig = (updates: Partial<PagesConfigType>) => {
        setConfig(prevConfig => {
            const newConfig = { 
                ...prevConfig, 
                ...updates,
                id: prevConfig.id 
            };
            if (onConfigChange) {
                onConfigChange(newConfig);
            }
            return newConfig;
        });
    };

    const handlePageSpacingChange = (value: number) => {
        setLocalPageSpacing(value);
        
        if (pageSpacingTimerRef.current) {
            clearTimeout(pageSpacingTimerRef.current);
        }
        
        pageSpacingTimerRef.current = setTimeout(() => {
            updateConfig({ pageSpacing: value });
        }, 500);
    };

    const handleGroupSpacingChange = (value: number) => {
        setLocalGroupSpacing(value);
        
        if (groupSpacingTimerRef.current) {
            clearTimeout(groupSpacingTimerRef.current);
        }
        
        groupSpacingTimerRef.current = setTimeout(() => {
            updateConfig({ groupSpacing: value });
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (pageSpacingTimerRef.current) {
                clearTimeout(pageSpacingTimerRef.current);
            }
            if (groupSpacingTimerRef.current) {
                clearTimeout(groupSpacingTimerRef.current);
            }
        };
    }, []);

    const toggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    const handleOnDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const { source, destination, type } = result;

        if (type === 'group') {
            const newGroups = Array.from(groups);
            const [reorderedGroup] = newGroups.splice(source.index, 1);
            newGroups.splice(destination.index, 0, reorderedGroup);

            const reorderedGroups = newGroups.map((group, index) => ({
                ...group,
                order: index
            }));

            setGroups(reorderedGroups);
        } 
        else if (type === 'page') {
            const sourceGroupId = source.droppableId.replace('pages-', '');
            const destinationGroupId = destination.droppableId.replace('pages-', '');
            
            if (sourceGroupId === destinationGroupId) {
                const newGroups = groups.map(group => {
                    if (group.id === sourceGroupId) {
                        const newPages = Array.from(group.pages);
                        const [reorderedPage] = newPages.splice(source.index, 1);
                        newPages.splice(destination.index, 0, reorderedPage);

                        const reorderedPages = newPages.map((page, index) => ({
                            ...page,
                            order: index
                        }));

                        return {
                            ...group,
                            pages: reorderedPages
                        };
                    }
                    return group;
                });

                setGroups(newGroups);
            } else {
                let movedPage: any = null;
                
                const newGroups = groups.map(group => {
                    if (group.id === sourceGroupId) {
                        const newPages = [...group.pages];
                        movedPage = newPages.splice(source.index, 1)[0];
                        
                        const reorderedPages = newPages.map((page, index) => ({
                            ...page,
                            order: index
                        }));
                        
                        return {
                            ...group,
                            pages: reorderedPages
                        };
                    }
                    
                    return group;
                });

                const finalGroups = newGroups.map(group => {
                    if (group.id === destinationGroupId && movedPage) {
                        const newPages = [...group.pages];
                        newPages.splice(destination.index, 0, movedPage);
                        
                        const reorderedPages = newPages.map((page, index) => ({
                            ...page,
                            order: index
                        }));
                        
                        return {
                            ...group,
                            pages: reorderedPages
                        };
                    }
                    
                    return group;
                });
                
                setGroups(finalGroups);
                
                if (editorState.activePageId === movedPage?.id) {
                    setEditorState(prev => ({
                        ...prev,
                        activeGroupId: destinationGroupId
                    }));
                }
            }
        }
    };

    const handleDeleteGroup = (groupId: string, groupName: string) => {
        setDeleteConfirmation({
            isOpen: true,
            type: 'group',
            id: groupId,
            name: groupName
        });
    };

    const handleDeletePage = (pageId: string, groupId: string, pageName: string) => {
        setDeleteConfirmation({
            isOpen: true,
            type: 'page',
            id: pageId,
            parentId: groupId,
            name: pageName
        });
    };

    const handleDuplicatePage = async (pageId: string, groupId: string, pageName: string) => {
        try {
            const result = await duplicatePage(pageId, groupId, pageName);
            if (result.success) {
                toast.success(`Page "${pageName}" duplicated successfully`);
            } else {
                toast.error(`Failed to duplicate page: ${result.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Duplicate page error:', error);
            toast.error('Failed to duplicate page');
        }
    };

    const confirmDelete = async () => {
        if (!deleteConfirmation.id || !deleteConfirmation.type) return;

        setIsDeleting(true);
        
        try {
            let result;
            
            if (deleteConfirmation.type === 'group') {
                result = await deleteGroup(deleteConfirmation.id);
                if (result.success) {
                    toast.success(`Group "${deleteConfirmation.name}" deleted successfully`);
                } else {
                    toast.error(`Failed to delete group: ${result.error || 'Unknown error'}`);
                }
            } else if (deleteConfirmation.type === 'page' && deleteConfirmation.parentId) {
                const pageIdToDelete = deleteConfirmation.id;
                const canvasIdToDelete = canvasMap[pageIdToDelete];

                result = await deletePage(pageIdToDelete, deleteConfirmation.parentId);

                if (result.success) {
                    if (canvasIdToDelete && configureId && currentUIModelId) {
                        try {
                            await uiModelApiService.updateCanvas(configureId, currentUIModelId, { containers: [] }, canvasIdToDelete);
                        } catch (e) {
                            console.error('Failed to clean up canvas on server:', e);
                        }
                    }

                    if (canvasIdToDelete) {
                        setCanvas(prev => {
                            const newCanvas = { ...prev };
                            delete newCanvas[canvasIdToDelete];
                            return newCanvas;
                        });
                    }
                    setContainers(prev => {
                        const newContainers = { ...prev };
                        delete newContainers[pageIdToDelete];
                        return newContainers;
                    });
                    setCanvasMap(prev => {
                        const newCanvasMap = { ...prev };
                        delete newCanvasMap[pageIdToDelete];
                        return newCanvasMap;
                    });
                    toast.success(`Page "${deleteConfirmation.name}" deleted successfully`);
                } else {
                    toast.error(`Failed to delete page: ${result.error || 'Unknown error'}`);
                }
            } else {
                throw new Error('Invalid delete configuration');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(`Failed to delete ${deleteConfirmation.type}`);
        }
        finally {
            setIsDeleting(false);
            setDeleteConfirmation({ isOpen: false, type: null, id: null, name: '' });
        }
    };

    const cancelDelete = () => {
        setDeleteConfirmation({ isOpen: false, type: null, id: null, name: '' });
    };

    return (
        <div id={baseId} data-testid={baseId} className={styles.container}>
            <div id={`${baseId}-header`} className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-pb-2 tw-my-4 tw-border-b tw-border-[#D1D1D6]">
                <h2 id={`${baseId}-title`} className="tw-text-sm tw-text-[#6D788C] tw-font-semibold">Navigation</h2>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <ToggleButton
                        id={`${baseId}-show-navigation-toggle`}
                        name="show-navigation"
                        value={config.showNavigation}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateConfig({ showNavigation: e.target.checked })}
                    />
                    <button
                        id={`${baseId}-collapse-btn`}
                        data-testid={`${baseId}-collapse-btn`}
                        onClick={toggleExpand}
                        className={styles.collapseButton}>
                        {isExpanded ? <UpArrow/> : <DownArrow/>}
                    </button>
                </div>
            </div>
            {isExpanded && (
                <>
                {/* Orientation */}
                <div id={`${baseId}-orientation-section`} className={styles.section}>
                    <h3 id={`${baseId}-orientation-label`} className={styles.subSectionTitle}>Orientation</h3>
                    <div id={`${baseId}-orientation-options`} className=" tw-rounded-md tw-flex tw-gap-4">
                        <label id={`${baseId}-orientation-horizontal-label`} className="tw-flex tw-items-center">
                            <input
                                id={`${baseId}-orientation-horizontal-radio`}
                                data-testid={`${baseId}-orientation-horizontal-radio`}
                                type="radio"
                                name="orientation"
                                value="Horizontal"
                                checked={config.orientation === 'Horizontal'}
                                onChange={() => updateConfig({ orientation: 'Horizontal' })}
                                className="tw-mr-2 tw-accent-[#091CAA]"
                            />
                            <span id={`${baseId}-orientation-horizontal-text`} className="tw-text-sm tw-text-[#172B4D]">Horizontal</span>
                        </label>
                        <label id={`${baseId}-orientation-vertical-label`} className="tw-flex tw-items-center">
                            <input
                                id={`${baseId}-orientation-vertical-radio`}
                                data-testid={`${baseId}-orientation-vertical-radio`}
                                type="radio"
                                name="orientation"
                                value="Vertical"
                                disabled={true}
                                checked={config.orientation === 'Vertical'}
                                onChange={() => updateConfig({ orientation: 'Vertical' })}
                                className="tw-mr-2 tw-accent-[#091CAA]"
                            />
                            <span id={`${baseId}-orientation-vertical-text`} className="tw-text-sm tw-text-[#172B4D]">Vertical</span>
                        </label>
                    </div>
                </div>
                
                {/* Style */}
                <div id={`${baseId}-style-section`} className={styles.section}>
                    <h3 id={`${baseId}-style-label`} className={styles.subSectionTitle}>Style</h3>
                    <div id={`${baseId}-style-options`} className=" tw-rounded-md tw-flex tw-gap-4">
                        <label id={`${baseId}-style-block-label`} className="tw-flex tw-items-center">
                            <input
                                id={`${baseId}-style-block-radio`}
                                data-testid={`${baseId}-style-block-radio`}
                                type="radio"
                                name="style"
                                value="Block"
                                checked={config.style === 'Block'}
                                onChange={() => updateConfig({ style: 'Block' })}
                                className="tw-mr-2 tw-accent-[#091CAA]"
                            />
                            <span id={`${baseId}-style-block-text`} className="tw-text-sm tw-text-[#172B4D]">Block</span>
                        </label>
                        <label id={`${baseId}-style-stepper-label`} className="tw-flex tw-items-center">
                            <input
                                id={`${baseId}-style-stepper-radio`}
                                data-testid={`${baseId}-style-stepper-radio`}
                                type="radio"
                                name="style"
                                value="Stepper"
                                checked={config.style === 'Stepper'}
                                onChange={() => updateConfig({ style: 'Stepper' })}
                                className="tw-mr-2 tw-accent-[#091CAA]"
                            />
                            <span id={`${baseId}-style-stepper-text`} className="tw-text-sm tw-text-[#172B4D]">Stepper</span>
                        </label>
                    </div>
                </div>

                {/* Flow */}
                <div id={`${baseId}-flow-section`} className={styles.section}>
                    <h3 id={`${baseId}-flow-label`} className={styles.subSectionTitle}>Flow</h3>
                    <div id={`${baseId}-flow-options`} className=" tw-rounded-md tw-flex tw-gap-4">
                        <label id={`${baseId}-flow-linear-label`} className="tw-flex tw-items-center">
                            <input
                                id={`${baseId}-flow-linear-radio`}
                                data-testid={`${baseId}-flow-linear-radio`}
                                type="radio"
                                name="flow"
                                value="Linear"
                                checked={config.flow === 'Linear'}
                                onChange={() => updateConfig({ flow: 'Linear' })}
                                className="tw-mr-2 tw-accent-[#091CAA]"
                            />
                            <span id={`${baseId}-flow-linear-text`} className="tw-text-sm tw-text-[#172B4D]">Linear</span>
                        </label>
                        <label id={`${baseId}-flow-nonlinear-label`} className="tw-flex tw-items-center">
                            <input
                                id={`${baseId}-flow-nonlinear-radio`}
                                data-testid={`${baseId}-flow-nonlinear-radio`}
                                type="radio"
                                name="flow"
                                value="Nonlinear"
                                checked={config.flow === 'Nonlinear'}
                                onChange={() => updateConfig({ flow: 'Nonlinear' })}
                                className="tw-mr-2 tw-accent-[#091CAA]"
                            />
                            <span id={`${baseId}-flow-nonlinear-text`} className="tw-text-sm tw-text-[#172B4D]">Nonlinear</span>
                        </label>
                    </div>
                </div>

                <div id={`${baseId}-divider-section`} className={styles.section}>
                    <div id={`${baseId}-divider-wrapper`} className="tw-py-2 tw-rounded-md">
                        <label id={`${baseId}-divider-label`} className={styles.checkboxContainer}>
                            <input
                                id={`${baseId}-divider-checkbox`}
                                data-testid={`${baseId}-divider-checkbox`}
                                type="checkbox"
                                checked={config.showDivider}
                                onChange={(e) => updateConfig({ showDivider: e.target.checked })}
                                className={styles.checkboxInput}
                            />
                            <span id={`${baseId}-divider-text`} className={styles.checkboxLabel}>Show Divider between page groups</span>
                        </label>
                    </div>
                </div>

                <div id={`${baseId}-page-spacing-section`} className={styles.section}>
                    <h3 id={`${baseId}-page-spacing-label`} className={styles.subSectionTitle}>Page Spacing</h3>
                    <div id={`${baseId}-page-spacing-wrapper`} className=" tw-rounded-md">
                        <div id={`${baseId}-page-spacing-slider-container`} className={styles.sliderContainer}>
                            <Slider
                                disable={false}
                                value={localPageSpacing}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    handlePageSpacingChange(value);
                                }}
                                min={4}
                                max={24}
                            />
                            <div id={`${baseId}-page-spacing-input-container`} className={styles.numberInputContainer}>
                                <input
                                    id={`${baseId}-page-spacing-input`}
                                    data-testid={`${baseId}-page-spacing-input`}
                                    type="number"
                                    value={localPageSpacing}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);

                                        if (isNaN(value) || e.target.value === '') {
                                            handlePageSpacingChange(4);
                                            return;
                                        }

                                        const clampedValue = Math.min(Math.max(value, 4), 24);
                                        handlePageSpacingChange(clampedValue);
                                    }}
                                    onBlur={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (isNaN(value) || value < 4) {
                                            handlePageSpacingChange(4);
                                        } else if (value > 24) {
                                            handlePageSpacingChange(24);
                                        }
                                    }}
                                    min="4"
                                    max="24"
                                    className={styles.numberInput}
                                />
                                <span id={`${baseId}-page-spacing-unit`} className={styles.unit}>px</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div id={`${baseId}-group-spacing-section`} className={styles.section}>
                    <h3 id={`${baseId}-group-spacing-label`} className={styles.subSectionTitle}>Group Spacing</h3>
                    <div id={`${baseId}-group-spacing-wrapper`} className="tw-rounded-md">
                        <div id={`${baseId}-group-spacing-slider-container`} className={styles.sliderContainer}>
                            <Slider
                                disable={false}
                                value={localGroupSpacing}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    handleGroupSpacingChange(value);
                                }}
                                min={4}
                                max={24}
                            />
                            <div id={`${baseId}-group-spacing-input-container`} className={styles.numberInputContainer}>
                                <input
                                    id={`${baseId}-group-spacing-input`}
                                    data-testid={`${baseId}-group-spacing-input`}
                                    type="number"
                                    value={localGroupSpacing}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);

                                        if (isNaN(value) || e.target.value === '') {
                                            handleGroupSpacingChange(4);
                                            return;
                                        }

                                        const clampedValue = Math.min(Math.max(value, 4), 24);
                                        handleGroupSpacingChange(clampedValue);
                                    }}
                                    onBlur={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (isNaN(value) || value < 4) {
                                            handleGroupSpacingChange(4);
                                        } else if (value > 24) {
                                            handleGroupSpacingChange(24);
                                        }
                                    }}
                                    min="4"
                                    max="24"
                                    className={styles.numberInput}
                                />
                                <span id={`${baseId}-group-spacing-unit`} className={styles.unit}>px</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div id={`${baseId}-groups-pages-section`} className={styles.section}>
                    <h3 id={`${baseId}-groups-pages-label`} className={styles.subSectionTitle}>Added Groups/Pages</h3>
                    <div id={`${baseId}-groups-pages-list`} className={`${styles.groupsList} tw-max-h-[300px] tw-overflow-y-auto tw-bg-[#F9FAFB] tw-rounded-md tw-p-2`}>
                        {config.groups && config.groups.length > 0 ? (
                            <DragDropContext onDragEnd={handleOnDragEnd}>
                                <Droppable droppableId="groups" type="group">
                                    {(provided) => (
                                        <div id={`${baseId}-groups-droppable`} {...provided.droppableProps} ref={provided.innerRef}>
                                            {groups.map((group, groupIndex) => (
                                                <Draggable key={group.id} draggableId={group.id} index={groupIndex}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            id={`${baseId}-group-${group.id}`}
                                                            data-testid={`${baseId}-group-${group.id}`}
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`tw-mb-2 tw-border tw-rounded tw-p-2 ${
                                                                snapshot.isDragging ? 'tw-bg-gray-100' : 'tw-bg-white'
                                                            }`}
                                                        >
                                                            <div id={`${baseId}-group-${group.id}-header`} className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                                                <div id={`${baseId}-group-${group.id}-title-wrapper`} className="tw-flex tw-items-center tw-gap-2">
                                                                    <div id={`${baseId}-group-${group.id}-drag-handle`} {...provided.dragHandleProps}>
                                                                        <DragIcon />
                                                                    </div>
                                                                    {/*I prefer this color #172B4D */}
                                                                    <span id={`${baseId}-group-${group.id}-name`} className="tw-font-semibold tw-text-xs tw-text-[#1F7AE4]">{group.name}</span>
                                                                </div>
                                                                
                                                                {/* Delete dropdown for groups - only show if more than 1 group */}
                                                                {groups.length > 1 && (
                                                                    <DropdownMenu
                                                                        trigger={<OverFlowIcon />}
                                                                        items={[
                                                                            {
                                                                                id: 'delete',
                                                                                label: 'Delete Group',
                                                                                icon: <DeleteIcon />,
                                                                                onClick: () => handleDeleteGroup(group.id, group.name),
                                                                                className: 'tw-text-red-600 hover:tw-bg-red-50'
                                                                            }
                                                                        ] as DropdownMenuItem[]}
                                                                    />
                                                                )}
                                                            </div>

                                                            {/* Pages within the group - Changed to use common type "page" */}
                                                            <Droppable droppableId={`pages-${group.id}`} type="page">
                                                                {(provided) => (
                                                                    <div
                                                                        id={`${baseId}-group-${group.id}-pages`}
                                                                        {...provided.droppableProps}
                                                                        ref={provided.innerRef}
                                                                        className="tw-ml-3 tw-min-h-[20px]"
                                                                    >
                                                                        {group.pages.map((page, pageIndex) => (
                                                                            <Draggable
                                                                                key={page.id}
                                                                                draggableId={page.id}
                                                                                index={pageIndex}
                                                                            >
                                                                                {(provided, snapshot) => (
                                                                                    <div
                                                                                        id={`${baseId}-page-${page.id}`}
                                                                                        data-testid={`${baseId}-page-${page.id}`}
                                                                                        ref={provided.innerRef}
                                                                                        {...provided.draggableProps}
                                                                                        className={`tw-flex tw-items-center tw-justify-between tw-p-2 tw-mb-1 tw-border tw-rounded ${
                                                                                            snapshot.isDragging ? 'tw-bg-gray-50' : 'tw-bg-white'
                                                                                        }`}
                                                                                    >
                                                                                        <div id={`${baseId}-page-${page.id}-title-wrapper`} className="tw-flex tw-items-center tw-gap-2">
                                                                                            <div id={`${baseId}-page-${page.id}-drag-handle`} {...provided.dragHandleProps}>
                                                                                                <DragIcon />
                                                                                            </div>
                                                                                            {/*I prefer this color #172B4D */}
                                                                                            <span id={`${baseId}-page-${page.id}-name`} className='tw-font-medium tw-text-xs tw-text-[#1F7AE4]'>{page.name}</span>
                                                                                        </div>
                                                                                        
                                                                                        {/* Dropdown menu for pages */}
                                                                                        <DropdownMenu
                                                                                            trigger={<OverFlowIcon />}
                                                                                            items={[
                                                                                                {
                                                                                                    id: 'duplicate',
                                                                                                    label: 'Duplicate Page',
                                                                                                    icon: <DuplicateIcon />,
                                                                                                    onClick: () => handleDuplicatePage(page.id, group.id, page.name),
                                                                                                    className: 'tw-text-[#172B4D] hover:tw-bg-gray-50',
                                                                                                    divider: group.pages.length > 1
                                                                                                },
                                                                                                ...(group.pages.length > 1 ? [{
                                                                                                    id: 'delete',
                                                                                                    label: 'Delete Page',
                                                                                                    icon: <DeleteIcon />,
                                                                                                    onClick: () => handleDeletePage(page.id, group.id, page.name),
                                                                                                    className: 'tw-text-red-600 hover:tw-bg-red-50'
                                                                                                }] : [])
                                                                                            ] as DropdownMenuItem[]}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </Draggable>
                                                                        ))}
                                                                        {provided.placeholder}
                                                                    </div>
                                                                )}
                                                            </Droppable>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        ) : (
                            <div id={`${baseId}-empty-state`} className="tw-text-gray-400 tw-text-sm tw-text-center tw-py-4">
                                No groups or pages added yet
                            </div>
                        )}
                    </div>
                </div>
                </>
            )}
            <ConfirmationDialog
                id={`${baseId}-delete-confirmation`}
                isOpen={deleteConfirmation.isOpen}
                title={`Delete ${deleteConfirmation.type === 'group' ? 'Group' : 'Page'}`}
                message={`Are you sure you want to delete "${deleteConfirmation.name}"? ${
                    deleteConfirmation.type === 'group'
                        ? 'All pages and containers within this group will also be deleted.'
                        : 'All containers within this page will also be deleted.'
                } This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                isLoading={isDeleting}
            />
        </div>

    );
    
};


const styles = {
    container: "",
    header: `${globalStyles.sectionTitle} tw-flex tw-justify-between tw-items-center `,
    sectionTitle: `${globalStyles.checkboxLabel} tw-mt-3`,
    collapseButton: "tw-rounded tw-bg-gainsboro-100 tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center",
    section: globalStyles.section,
    subSectionTitle: globalStyles.subSectionTitle,
    checkboxContainer: "tw-flex tw-items-center",
    checkboxInput: globalStyles.checkboxInput,
    checkboxLabel: "tw-text-[#172B4D] tw-text-sm",
    sliderContainer: "tw-flex tw-items-center tw-space-x-2",
    numberInputContainer: "tw-flex tw-items-center tw-text-[#091E42]",
    numberInput: "tw-w-14 tw-border tw-border-[#97A0AF] tw-rounded tw-px-2 tw-py-1 tw-text-xs",
    unit: "tw-ml-2",
    groupsList: "tw-space-y-2",
    groupItem: "tw-flex tw-items-center tw-justify-between tw-py-2 tw-border-b tw-border-[#D1D1D6]",
    groupItemContent: "tw-flex tw-items-center",
    groupIcon: "tw-mr-2",
    groupName: "tw-text-[#1F7AE4]",
    menuButton: "tw-p-1",
    pageItem: "tw-flex tw-items-center tw-justify-between tw-py-2"
};